import { getURLWithFullPageInvoice } from '~/components/FullPageInvoice/FullPageInvoice.utils';
import { routes } from '~/constants';
import { MoneyModel } from '~/models';
import { RelationshipStatus } from '~/models/relationship';
import { AccountNotificationType, BusinessNotificationType, InvoiceNotificationType, NotificationType, RelationshipNotificationType } from '~/types/api/notification.model';
import { makeAfterEmailVerification, makeOpenMandateReauth, makeScrollToBottom } from '~/utils/location-state';
import { NotificationActionType } from '../NotificationAction/NotificationActionTypes';
export const unrecognizedNotification = 'unrecognized notification';
export const incorrectNotificationPayload = 'incorrect notification payload';
const defaultRedirect = {
    url: routes.home
};
const makeBadNotificationPayloadRedirect = ()=>({
        error: incorrectNotificationPayload,
        redirect: defaultRedirect
    });
const agreementViewStatuses = [
    RelationshipStatus.Approved,
    RelationshipStatus.Terminated,
    RelationshipStatus.AdHoc
];
// WHY: AgreementMinisite renders these three and silently redirects home for anything else.
const hasAgreementView = (status)=>agreementViewStatuses.some((agreementViewStatus)=>agreementViewStatus === status);
export function handleProposalNotification(notification, userInfo) {
    switch(notification.relationship.status){
        case 'pending':
            return {
                redirect: {
                    url: routes.clientOnboarding(notification.relationship.id)
                }
            };
        case 'canceled':
            {
                const { type, value } = notification.relationship.detailedStatus;
                switch(type){
                    case 'canceledWithSingle':
                        return {
                            redirect: {
                                url: routes.clientOnboarding(value === null || value === void 0 ? void 0 : value.relationshipId)
                            }
                        };
                    case 'canceledWithMulti':
                        return {
                            redirect: {
                                url: userInfo && userInfo.isReviewer() ? routes.canceledProposalPage : routes.proposalsDashboard
                            }
                        };
                    case 'canceledWithZero':
                    default:
                        return {
                            redirect: {
                                url: routes.canceledProposalPage
                            }
                        };
                }
            }
        default:
            return {
                redirect: {
                    url: routes.clientOnboarding(notification.relationship.id)
                }
            };
    }
}
function makeBlockedBankAccountRedirect(notification) {
    var _ref;
    var _notification_fundingSourceLapseScope;
    const [firstAffectedRelationshipId = notification.relationship.id] = (_ref = (_notification_fundingSourceLapseScope = notification.fundingSourceLapseScope) === null || _notification_fundingSourceLapseScope === void 0 ? void 0 : _notification_fundingSourceLapseScope.relationshipIds) !== null && _ref !== void 0 ? _ref : [];
    return makePaymentMethodsTabRedirect(firstAffectedRelationshipId);
}
function makeAffectedAgreementRedirect(notification) {
    var _ref;
    var _notification_fundingSourceLapseScope;
    const [firstAffectedRelationshipId] = (_ref = (_notification_fundingSourceLapseScope = notification.fundingSourceLapseScope) === null || _notification_fundingSourceLapseScope === void 0 ? void 0 : _notification_fundingSourceLapseScope.relationshipIds) !== null && _ref !== void 0 ? _ref : [];
    return firstAffectedRelationshipId == null ? {
        redirect: {
            url: routes.home
        }
    } : makePaymentMethodsTabRedirect(firstAffectedRelationshipId);
}
function makePaymentMethodsTabRedirect(relationshipId) {
    let { openMandateReauth = false } = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
    return {
        redirect: {
            url: routes.agreementMinisiteRoutes(relationshipId).paymentMethods,
            state: openMandateReauth ? makeOpenMandateReauth(true) : undefined
        }
    };
}
function makeAdHocRedirect(param) {
    let { url, notification, notificationId } = param;
    return {
        redirect: {
            url: "".concat(url, "?nid=").concat(encodeURIComponent(notificationId)),
            state: {
                notificationPayload: notification
            }
        }
    };
}
export function notificationRouter(param) {
    let { notification, claims, userInfo, notificationId } = param;
    switch(notification.type){
        case NotificationType.Relationship:
            {
                if (!notification.relationship.id) {
                    return makeBadNotificationPayloadRedirect();
                }
                switch(notification.notificationType){
                    case RelationshipNotificationType.AdHocChargeAdded:
                    case RelationshipNotificationType.AdHocChargeCanceled:
                    case RelationshipNotificationType.AdHocChargeApprovalReminder:
                    case RelationshipNotificationType.CreditApplied:
                        return {
                            redirect: {
                                url: routes.agreementMinisiteRoutes(notification.relationship.id).billingHub
                            }
                        };
                    case RelationshipNotificationType.Amended:
                    case RelationshipNotificationType.AmendedNoClientApproval:
                    case RelationshipNotificationType.Approved:
                    case RelationshipNotificationType.RelationshipApprovedCustomerOld:
                    case RelationshipNotificationType.RelationshipApprovedVendor:
                    case RelationshipNotificationType.ApprovedAmendments:
                    case RelationshipNotificationType.RelationshipAmendmentsApprovedVendor:
                    case RelationshipNotificationType.ClientUnresponsiveAmendmentsApproval:
                    case RelationshipNotificationType.ProposalNotSignedVendor:
                    case RelationshipNotificationType.ClientUnresponsiveSigning:
                    case RelationshipNotificationType.AgreementAmendmentsReminder:
                    case RelationshipNotificationType.RelationshipApprovedCustomer:
                    case RelationshipNotificationType.Terminated:
                    case RelationshipNotificationType.RelationshipUpdated:
                    case RelationshipNotificationType.RelationshipUpdatedWithChanges:
                    case RelationshipNotificationType.AmendmentsAutoApprovalReminder:
                    case RelationshipNotificationType.AmendmentsAutoApproved:
                    case RelationshipNotificationType.RelationshipTerminated:
                    case RelationshipNotificationType.RelationshipAutoPriceIncreaseVendor:
                    case RelationshipNotificationType.RelationshipAutoPriceIncreaseCustomer:
                    case RelationshipNotificationType.RelationshipPracticeManagementFailedItems:
                    case RelationshipNotificationType.RelationshipPracticeManagementContactMatchFailure:
                    case RelationshipNotificationType.AmendmentNotApprovedVendor:
                    case RelationshipNotificationType.AmendmentApprovalReminderCustomer:
                    case RelationshipNotificationType.BankAccountNotVerifiedReminderVendor:
                        return {
                            redirect: {
                                url: routes.agreementMinisiteRoutes(notification.relationship.id).agreement
                            }
                        };
                    case RelationshipNotificationType.RelationshipTaxRateChangedCustomer:
                    case RelationshipNotificationType.ClientTaxExemptionGrantedCustomer:
                    case RelationshipNotificationType.ClientTaxExemptionRemovedCustomer:
                        if (!hasAgreementView(notification.relationship.status)) {
                            return handleProposalNotification(notification, userInfo);
                        }
                        return {
                            redirect: {
                                url: routes.agreementMinisiteRoutes(notification.relationship.id).agreement
                            }
                        };
                    case RelationshipNotificationType.BankAccountAuthorizationRequiredCustomer:
                        if (notification.relationship.status === RelationshipStatus.AdHoc) {
                            return makeAdHocRedirect({
                                url: routes.adHocBankVerification(notification.relationship.id),
                                notification,
                                notificationId
                            });
                        }
                        return makePaymentMethodsTabRedirect(notification.relationship.id, {
                            openMandateReauth: true
                        });
                    case RelationshipNotificationType.BankAccountBlockedCustomer:
                        return makeBlockedBankAccountRedirect(notification);
                    case RelationshipNotificationType.ClientUnresponsivePaymentMethod:
                    case RelationshipNotificationType.MicroDepositCompletionReminder:
                    case RelationshipNotificationType.MicroDepositProcessInitiated:
                    case RelationshipNotificationType.BankAccountNotVerifiedReminderCustomer:
                    case RelationshipNotificationType.RequestPaymentMethod:
                        return makePaymentMethodsTabRedirect(notification.relationship.id);
                    case RelationshipNotificationType.AdHocInvoiceBankAccountVerificationReminder:
                        return makeAdHocRedirect({
                            url: routes.adHocBankVerification(notification.relationship.id),
                            notification,
                            notificationId
                        });
                    case RelationshipNotificationType.RelationshipDirectLink:
                        {
                            const relationshipEntity = notification.relationship.directLinkData;
                            let url = routes.home;
                            if (relationshipEntity) {
                                if ('invoice' in relationshipEntity) {
                                    const invoiceId = relationshipEntity.invoice;
                                    const relationshipId = notification.relationship.id;
                                    url = getURLWithFullPageInvoice({
                                        pathname: routes.invoicesDashboard,
                                        invoiceId,
                                        relationshipId
                                    });
                                } else if ('agreement' in relationshipEntity) {
                                    url = routes.agreementMinisiteRoutes(notification.relationship.id).agreement;
                                }
                            }
                            return {
                                redirect: {
                                    url
                                }
                            };
                        }
                    case RelationshipNotificationType.Added:
                    case RelationshipNotificationType.New:
                    case RelationshipNotificationType.NewWithText:
                    case RelationshipNotificationType.ExistingCustomer:
                    case RelationshipNotificationType.AddedWithCustomText:
                    case RelationshipNotificationType.SingleUseApproval:
                    case RelationshipNotificationType.ExistingCustomerWithText:
                    case RelationshipNotificationType.RelationshipNewProposalCustomer:
                    case RelationshipNotificationType.ProposalUpdatedCustomer:
                    case RelationshipNotificationType.AgreementApprovalReminder:
                    case RelationshipNotificationType.AgreementApprovalReminderManual:
                    case RelationshipNotificationType.RelationshipSigning:
                    case RelationshipNotificationType.RelationshipReadyForReview:
                    case RelationshipNotificationType.ReviewProposalLink:
                    case RelationshipNotificationType.ProposalRepublishedNotifyReviewers:
                    case RelationshipNotificationType.ProposalRepublishedNotifySignatories:
                    case RelationshipNotificationType.ProposalApprovalManualReminder:
                    case RelationshipNotificationType.SharedProposalLink:
                        return handleProposalNotification(notification, userInfo);
                    case RelationshipNotificationType.ApproveAmendment:
                        if (notification.relationship.approvalToken) {
                            const state = {
                                type: NotificationActionType.ApproveAmendment,
                                token: notification.relationship.approvalToken,
                                vendorName: notification.relationship.vendorName
                            };
                            return {
                                redirect: {
                                    url: routes.notificationAction,
                                    state
                                }
                            };
                        }
                        return {
                            error: unrecognizedNotification,
                            redirect: {
                                url: routes.agreementMinisiteRoutes(notification.relationship.id).agreement
                            }
                        };
                    default:
                        return {
                            error: unrecognizedNotification,
                            redirect: {
                                url: routes.agreementMinisiteRoutes(notification.relationship.id).agreement
                            }
                        };
                }
            }
        case NotificationType.Invoice:
            {
                if (!notification.invoice.relationshipId) {
                    return makeBadNotificationPayloadRedirect();
                }
                const { relationshipId, id: invoiceId, hasActivePaymentMethod = false } = notification.invoice;
                switch(notification.notificationType){
                    case InvoiceNotificationType.AdHocInvoiceBankAccountBlocked:
                    case InvoiceNotificationType.AdHocInvoiceIssued:
                    case InvoiceNotificationType.AdHocInvoiceOverdueReminderCustomer:
                    case InvoiceNotificationType.AdHocPaymentActionRequiredCustomer:
                    case InvoiceNotificationType.RetryAdHocPayment:
                        return makeAdHocRedirect({
                            url: routes.adHocInvoiceApproval(invoiceId),
                            notification,
                            notificationId
                        });
                    case InvoiceNotificationType.InvoiceIssued:
                        return {
                            redirect: {
                                url: hasActivePaymentMethod ? getURLWithFullPageInvoice({
                                    pathname: routes.invoicesDashboard,
                                    invoiceId,
                                    relationshipId
                                }) : routes.agreementMinisiteRoutes(relationshipId).paymentMethods
                            }
                        };
                    case InvoiceNotificationType.InvoiceIssuedOLD:
                    case InvoiceNotificationType.InvoiceIssuedRequiresApproval:
                    case InvoiceNotificationType.PaidCustomer:
                    case InvoiceNotificationType.PaymentProcessingCustomer:
                    case InvoiceNotificationType.PaymentProcessingVendor:
                    case InvoiceNotificationType.UpcomingForImmediatePayment:
                    case InvoiceNotificationType.ExtensionRequested:
                    case InvoiceNotificationType.OneOffIssuedRequiresApproval:
                    case InvoiceNotificationType.InvoiceApproved:
                    case InvoiceNotificationType.ProcessingActionPending:
                    case InvoiceNotificationType.InvoiceIssuedForPayment:
                        return {
                            redirect: {
                                url: routes.agreementMinisiteRoutes(relationshipId).paymentMethods
                            }
                        };
                    case InvoiceNotificationType.PaymentFailed:
                        return {
                            redirect: {
                                url: routes.agreementMinisiteRoutes(relationshipId).agreement,
                                state: makeScrollToBottom(true)
                            }
                        };
                    case InvoiceNotificationType.InvoicePaymentStoppedCustomer:
                    case InvoiceNotificationType.InvoicePaymentStoppedVendor:
                    case InvoiceNotificationType.InvoicePaymentResumedVendor:
                    case InvoiceNotificationType.InvoicePaymentResumedCustomer:
                    case InvoiceNotificationType.InvoiceRefundedVendor:
                    case InvoiceNotificationType.InvoiceRefundFailedVendor:
                    case InvoiceNotificationType.InvoiceRefundedCustomer:
                    case InvoiceNotificationType.PaymentDisputedVendor:
                    case InvoiceNotificationType.PaymentFailedCustomer:
                    case InvoiceNotificationType.PaymentFailedVendor:
                    case InvoiceNotificationType.PaymentActionRequiredCustomer:
                        return {
                            redirect: {
                                url: getURLWithFullPageInvoice({
                                    pathname: routes.invoicesDashboard,
                                    invoiceId,
                                    relationshipId
                                })
                            }
                        };
                    case InvoiceNotificationType.ApproveInvoice:
                        if (notification.invoice.approvalToken) {
                            const state = {
                                type: NotificationActionType.ApproveInvoice,
                                token: notification.invoice.approvalToken,
                                vendorName: notification.invoice.vendorName,
                                invoiceFormattedNumber: notification.invoice.invoiceFormattedNumber,
                                invoiceFormattedAmount: MoneyModel.fromDTO(notification.invoice.amount).display()
                            };
                            return {
                                redirect: {
                                    url: routes.notificationAction,
                                    state
                                }
                            };
                        }
                        return {
                            error: unrecognizedNotification,
                            redirect: {
                                url: routes.agreementMinisiteRoutes(relationshipId).invoices
                            }
                        };
                    default:
                        return {
                            error: unrecognizedNotification,
                            redirect: {
                                url: routes.agreementMinisiteRoutes(relationshipId).invoices
                            }
                        };
                }
            }
        case NotificationType.Account:
            {
                switch(notification.notificationType){
                    case AccountNotificationType.EmailVerification:
                    case AccountNotificationType.CapitalizedEmailVerification:
                    case AccountNotificationType.MarketingSignUpEmailVerification:
                        return {
                            redirect: {
                                url: routes.home,
                                state: makeAfterEmailVerification(true)
                            },
                            hubspotEventName: 'notification_open_emailverification'
                        };
                    case AccountNotificationType.ContinueVendorOnboarding:
                    case AccountNotificationType.EmailUpdate:
                    case AccountNotificationType.OneTimeAccess:
                    case AccountNotificationType.VerificationPassed:
                    case AccountNotificationType.PasswordResetSuccessfully:
                        return {
                            redirect: {
                                url: routes.home
                            }
                        };
                    case AccountNotificationType.ResetPasswordRequested:
                    case AccountNotificationType.InviteToJoinBusiness:
                        {
                            var _notification_account;
                            const url = ((_notification_account = notification.account) === null || _notification_account === void 0 ? void 0 : _notification_account.user) ? routes.home : routes.setPassword;
                            return {
                                redirect: {
                                    url
                                }
                            };
                        }
                    case AccountNotificationType.InviteTeamMember:
                        {
                            var _notification_account1;
                            const claimsSetPasswordOnly = claims === null || claims === void 0 ? void 0 : claims.setPasswordOnly;
                            if ((_notification_account1 = notification.account) === null || _notification_account1 === void 0 ? void 0 : _notification_account1.teamInvitationPayload) {
                                var _notification_account2, _notification_account3, _notification_account4, _notification_account5, _notification_account6;
                                const state = {
                                    type: NotificationActionType.ApproveTeamMemberInvitation,
                                    invitingUserName: (_notification_account2 = notification.account) === null || _notification_account2 === void 0 ? void 0 : _notification_account2.teamInvitationPayload.invitingUserName,
                                    invitingUserEmail: (_notification_account3 = notification.account) === null || _notification_account3 === void 0 ? void 0 : _notification_account3.teamInvitationPayload.invitingUserEmail,
                                    invitingBusinessName: (_notification_account4 = notification.account) === null || _notification_account4 === void 0 ? void 0 : _notification_account4.teamInvitationPayload.invitingBusinessName,
                                    invitingBusinessId: (_notification_account5 = notification.account) === null || _notification_account5 === void 0 ? void 0 : _notification_account5.teamInvitationPayload.invitingBusinessId,
                                    invitationId: (_notification_account6 = notification.account) === null || _notification_account6 === void 0 ? void 0 : _notification_account6.teamInvitationPayload.invitationId
                                };
                                const url = claimsSetPasswordOnly ? routes.setPassword : routes.notificationAction;
                                return {
                                    redirect: {
                                        url,
                                        state
                                    }
                                };
                            }
                            return {
                                error: unrecognizedNotification,
                                redirect: {
                                    url: routes.home
                                }
                            };
                        }
                    default:
                        return {
                            error: unrecognizedNotification,
                            redirect: {
                                url: routes.home
                            }
                        };
                }
            }
        case NotificationType.Business:
            {
                switch(notification.notificationType){
                    case BusinessNotificationType.ReminderConnectKyc:
                    case BusinessNotificationType.PayoutReconciliation:
                    case BusinessNotificationType.PaymentsDigest:
                        return {
                            redirect: {
                                url: routes.vendorDashboard
                            }
                        };
                    case BusinessNotificationType.AccountantJoinRequest:
                        return {
                            redirect: {
                                url: routes.team
                            }
                        };
                    case BusinessNotificationType.AccountantJoinRequestApproved:
                    case BusinessNotificationType.AccountantJoinRequestRejected:
                        return {
                            redirect: {
                                url: routes.contacts
                            }
                        };
                    case BusinessNotificationType.AccountantPmAdded:
                    case BusinessNotificationType.ClientTaxExemptionGrantedCustomer:
                    case BusinessNotificationType.ClientTaxExemptionRemovedCustomer:
                        return {
                            redirect: {
                                url: routes.agreementsDashboard
                            }
                        };
                    case BusinessNotificationType.ReferringVendorCreditsAwarded:
                        return {
                            redirect: {
                                url: routes.referrals
                            }
                        };
                    case BusinessNotificationType.ReferredVendorCreditsAwarded:
                        return {
                            redirect: {
                                url: routes.home
                            }
                        };
                    case BusinessNotificationType.PayoutDepositedVendor:
                    case BusinessNotificationType.PayoutFailedVendor:
                    case BusinessNotificationType.PayoutDepositedRetryVendor:
                        return {
                            redirect: {
                                url: routes.payoutsDashboard
                            }
                        };
                    case BusinessNotificationType.ManualMicroDepositInitiatedCustomer:
                    case BusinessNotificationType.ManualMicroDepositInitiatedVendor:
                    case BusinessNotificationType.BankAccountVerifiedSuccessfully:
                        return {
                            redirect: {
                                url: routes.home
                            }
                        };
                    case BusinessNotificationType.AdditionalPaymentProcessorInformationNeeded:
                        {
                            const { additionalPaymentProcessorInformationNeeded } = notification.business || {};
                            if (!additionalPaymentProcessorInformationNeeded) {
                                return makeBadNotificationPayloadRedirect();
                            }
                            const state = {
                                data: additionalPaymentProcessorInformationNeeded
                            };
                            return {
                                redirect: {
                                    url: routes.additionalPaymentProcessorInfo,
                                    state
                                }
                            };
                        }
                    case BusinessNotificationType.TaxForm1099Vendor:
                        {
                            return {
                                redirect: {
                                    url: routes.businessDetails
                                }
                            };
                        }
                    case BusinessNotificationType.IntegrationAutomaticallyDisconnected:
                        {
                            return {
                                redirect: {
                                    url: routes.availableApps
                                }
                            };
                        }
                    case BusinessNotificationType.ReconnectMailbox:
                        {
                            return {
                                redirect: {
                                    url: routes.notifications
                                }
                            };
                        }
                    case BusinessNotificationType.KybApprovedWithIdentity:
                    case BusinessNotificationType.KybApprovedRequiresIdentity:
                        return {
                            redirect: {
                                url: routes.home
                            }
                        };
                    case BusinessNotificationType.BankAccountAuthorizationRequiredVendor:
                    case BusinessNotificationType.BankAccountBlockedVendor:
                        return makeAffectedAgreementRedirect(notification);
                    default:
                        return {
                            error: unrecognizedNotification,
                            redirect: {
                                url: routes.home
                            }
                        };
                }
            }
        default:
            return {
                error: unrecognizedNotification,
                redirect: defaultRedirect
            };
    }
}
